import { Box, Button, Checkbox, FormControlLabel, TextField, Typography, Autocomplete, CircularProgress, Popper, Paper, ListItem, List, ListItemText, InputAdornment, FormControl, InputLabel, MenuItem, Select, useTheme } from '@mui/material';
import { Scroll, MagnifyingGlass } from '@phosphor-icons/react';
import { useState, useEffect, useRef } from 'react';
import KitModal from '../UI/KitModal';
import LoadingSpinner from '../UI/LoadingSpinner';
import { FlowService } from '../../service/FlowService';
import { useDebounceEffect } from '../../util/useDebounceEffect';
import { UserService } from '../../service/UserService';
import { useDarkMode } from '../../hooks/useDarkMode';

type Props = {
  onHide: () => void;
  show: boolean;
  flow?: any;
  isCreateFormLoading: boolean;
  onCreateForm: (name: string, useAgent: boolean, flowId: string, spaceId?: string) => void;
  selectedSpaceId?: string;
  onSpaceChange?: (spaceId: string) => void;
  showSpaceSelector?: boolean;
};

const CreateFormModal = ({
  onHide,
  show,
  flow: initialFlow,
  isCreateFormLoading,
  onCreateForm,
  selectedSpaceId = "",
  onSpaceChange,
  showSpaceSelector = false,
}: Props) => {
  const [name, setName] = useState('');
  const [useAgent, setUseAgent] = useState(false);
  const [selectedFlow, setSelectedFlow] = useState<any>(initialFlow || null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchAnchorEl, setSearchAnchorEl] = useState<HTMLElement | null>(null);
  const searchRef = useRef<HTMLDivElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const { customColorPalette: palette } = theme;
  const isDarkMode = useDarkMode();
  const [spaces, setSpaces] = useState<any[]>([]);
  const [localSelectedSpaceId, setLocalSelectedSpaceId] = useState<string>(selectedSpaceId || "private");

  useEffect(() => {
    if (initialFlow) {
      setSelectedFlow(initialFlow);
      setSearchTerm(initialFlow.name || '');
    }
  }, [initialFlow]);

  useEffect(() => {
    if (show) {
      loadSpaces();
    }
  }, [show]);

  const loadSpaces = async () => {
    try {
      const { data } = await UserService.getSpaces();
      setSpaces(data);
    } catch (error) {
      console.error('Failed to load spaces:', error);
    }
  };

  useEffect(() => {
    if (selectedSpaceId !== undefined) {
      setLocalSelectedSpaceId(selectedSpaceId);
    }
  }, [selectedSpaceId]);

  useDebounceEffect(
    () => {
      const searchFlows = async () => {
        if (!searchTerm.trim()) {
          setSearchResults([]);
          setIsSearching(false);
          return;
        }

        try {
          setIsSearching(true);
          const { data } = await FlowService.searchFlow(searchTerm);
          const agentsOnly = data.filter((item: any) => !item.isForm);
          setSearchResults(agentsOnly);
        } catch (error) {
          console.error('Error searching flows:', error);
          setSearchResults([]);
        } finally {
          setIsSearching(false);
        }
      };

      searchFlows();
    },
    [searchTerm],
    500,
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchAnchorEl &&
        searchRef.current &&
        !searchRef.current.contains(event.target as Node) &&
        resultsRef.current &&
        !resultsRef.current.contains(event.target as Node)
      ) {
        setSearchAnchorEl(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [searchAnchorEl]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchTerm(value);
    if (value) {
      setSearchAnchorEl(event.currentTarget);
    } else {
      setSearchAnchorEl(null);
      setSearchResults([]);
    }
  };

  const handleSearchFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    if (searchTerm && searchResults.length > 0) {
      setSearchAnchorEl(event.currentTarget);
    }
  };

  const handleFlowSelect = (flow: any) => {
    setSelectedFlow(flow);
    setSearchTerm(flow.name || '');
    setSearchAnchorEl(null);
    setSearchResults([]);
  };

  const handleClose = () => {
    setName('');
    setUseAgent(false);
    setSelectedFlow(initialFlow || null);
    setSearchTerm(initialFlow?.name || '');
    setSearchResults([]);
    setSearchAnchorEl(null);
    onHide();
  };

  const handleSpaceChange = (spaceId: string) => {
    setLocalSelectedSpaceId(spaceId);
    if (onSpaceChange) {
      onSpaceChange(spaceId === "private" ? "" : spaceId);
    }
  };

  const handleCreate = () => {
    if (!selectedFlow) {
      return;
    }
    const actualSpaceId = localSelectedSpaceId === "private" ? undefined : localSelectedSpaceId;
    onCreateForm(name, useAgent, selectedFlow.id, actualSpaceId);
    handleClose();
  };

  return (
    <KitModal
      onHide={handleClose}
      show={show}
      fullWidth
      sx={{
        '& .MuiPaper-root': {
          maxWidth: '520px',
          borderRadius: '8px',
        },
        '& .MuiDialogContent-root': {
          padding: '32px',
        },
        '& .MuiDialogActions-root': {
          padding: '32px',
        },
      }}
      showCloseButton={false}
    >
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        gap="1rem"
      >
        <Box display="flex" gap="16px" alignItems="center">
          <Box
            p={'2px'}
            bgcolor={'#E7F0FF'}
            display={'flex'}
            alignContent={'center'}
            justifyContent={'center'}
          >
            <Scroll color="#3650DE" size={32} />
          </Box>
          <Typography fontSize={'32px'} color={'#101828'}>
            Create your interface
          </Typography>
        </Box>
        <Typography
          sx={{
            color: '#475467',
            fontSize: '16px',
            fontWeight: 400,
          }}
        >
          {selectedFlow
            ? `You're crafting an interface based on the "${selectedFlow.name || 'Untitled'}" existing agent.`
            : 'Search for an agent to base your interface on.'}
        </Typography>
        
        <Box sx={{ position: 'relative' }} ref={searchRef}>
          <TextField
            fullWidth
            label="Search Agent"
            placeholder="Search for an agent..."
            value={searchTerm}
            onChange={handleSearchChange}
            onFocus={handleSearchFocus}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MagnifyingGlass size={20} />
                </InputAdornment>
              ),
              endAdornment: isSearching ? (
                <InputAdornment position="end">
                  <CircularProgress size={20} />
                </InputAdornment>
              ) : null,
            }}
            sx={{ marginBottom: 2 }}
          />
          
          {searchAnchorEl && searchResults.length > 0 && (
            <Popper
              open={true}
              anchorEl={searchAnchorEl}
              placement="bottom-start"
              style={{ width: searchAnchorEl.clientWidth, zIndex: 1300 }}
            >
              <Paper
                ref={resultsRef}
                sx={{
                  maxHeight: 300,
                  overflow: 'auto',
                  mt: 1,
                }}
              >
                <List>
                  {searchResults.map((result) => (
                    <ListItem
                      key={result.id}
                      button
                      onClick={() => handleFlowSelect(result)}
                      sx={{
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        },
                      }}
                    >
                      <ListItemText primary={result.name || 'Untitled'} />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Popper>
          )}
        </Box>

        <TextField
          id="outlined-basic"
          label="Enter Interface Name"
          variant="outlined"
          placeholder="Interface's name"
          type="text"
          name="name"
          value={name}
          onChange={e => setName(e.target.value)}
          fullWidth
        />

        {showSpaceSelector && (
          <FormControl fullWidth required>
            <InputLabel>Select Space</InputLabel>
            <Select
              value={localSelectedSpaceId}
              onChange={(e) => handleSpaceChange(e.target.value)}
              label="Select Space"
              disabled={isCreateFormLoading}
              sx={{
                backgroundColor: isDarkMode ? palette.gray[800] : '#FFFFFF',
                color: isDarkMode ? palette.gray.A100 : palette.gray[900],
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: isDarkMode ? palette.gray[700] : palette.gray[300],
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: isDarkMode ? palette.gray[600] : palette.gray[400],
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: palette.blue[500],
                },
                '& .MuiInputLabel-root': {
                  color: isDarkMode ? palette.gray[400] : palette.gray[600],
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: palette.blue[500],
                },
              }}
            >
              <MenuItem 
                value="private"
                sx={{
                  color: isDarkMode ? palette.gray.A100 : palette.gray[900],
                  '&:hover': {
                    backgroundColor: isDarkMode ? palette.gray[700] : palette.gray[100],
                  },
                }}
              >
                Private
              </MenuItem>
              {spaces.map((space) => (
                <MenuItem 
                  key={space.id} 
                  value={space.id}
                  sx={{
                    color: isDarkMode ? palette.gray.A100 : palette.gray[900],
                    '&:hover': {
                      backgroundColor: isDarkMode ? palette.gray[700] : palette.gray[100],
                    },
                  }}
                >
                  {space.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={useAgent}
                onChange={e => setUseAgent(e.target.checked)}
              />
            }
            label='Use the form builder agent'
          />
          <Typography
            sx={{
              color: '#475467',
              fontSize: '14px',
              fontWeight: 400,
              marginLeft: '32px',
              marginTop: '-8px',
            }}
          >
            The agent will create a form tailored to the flow. This may take a while.
          </Typography>
        </Box>

        <Button
          disabled={!name || !selectedFlow || isCreateFormLoading}
          fullWidth
          onClick={handleCreate}
          variant={`contained`}
          size="small"
        >
          {isCreateFormLoading ? (
            <>
              <LoadingSpinner size={16} />
            </>
          ) : (
            'Continue'
          )}
        </Button>
      </Box>
    </KitModal>
  );
};

export default CreateFormModal;
